/**
 * Conversation Actions types. Orders and returns are independent actions:
 * the admin picks the type when creating an action, the agent picks which
 * button to run, and each runs its own analysis and webhook. Every other
 * instance field (catalog, webhook, caps, business context, custom fields)
 * is shared. SEND_FILES (offered per org) runs no analysis and has no
 * catalog: the agent picks conversation files and the webhook receives
 * download links. AI_REPLY (offered per org) drafts or improves the agent's
 * reply in the reply box: no catalog, no webhook, no custom fields; it stores
 * instructions (in businessContext) and a fallback reply language instead.
 * CONVERSATION_INFO (offered per org, not billed) shows participant data in
 * Conversation Details and an entry notification: no catalog, webhook, custom
 * fields or business context; it stores its info fields instead.
 * Add an entry here (and its keys in en-us.json) when a type ships.
 */
import { t } from './language-manager';

export type ActionType = 'CREATE_ORDER' | 'CREATE_RETURN' | 'SEND_FILES' | 'AI_REPLY' | 'CONVERSATION_INFO';

export const ACTION_TYPES: ActionType[] = ['CREATE_ORDER', 'CREATE_RETURN', 'SEND_FILES', 'AI_REPLY', 'CONVERSATION_INFO'];
/** Type read for a stored instance with no `type` (rows from before types were orders). New actions start with none. */
export const DEFAULT_ACTION_TYPE: ActionType = 'CREATE_ORDER';

export function isActionType(value: string): value is ActionType {
  return (ACTION_TYPES as string[]).includes(value);
}

/** No analysis, no catalog, no business context: the payload carries files instead of items. */
export function isSendFiles(type: string): boolean {
  return type === 'SEND_FILES';
}

/** No catalog, webhook, secret, auth, timeout, test webhook, sample payload or custom fields. */
export function isAiReply(type: string): boolean {
  return type === 'AI_REPLY';
}

/** No catalog, webhook, secret, auth, timeout, test webhook, sample payload, custom fields or business context; it has info fields. */
export function isConversationInfo(type: string): boolean {
  return type === 'CONVERSATION_INFO';
}

/** Every type but AI Reply and Conversation info delivers to the org's webhook; a successful test is required before it is switched on. */
export function needsWebhook(type: string): boolean {
  return !isAiReply(type) && !isConversationInfo(type);
}

/** Orders and returns match against a product catalog; it must be loaded before the action is switched on. */
export function usesCatalog(type: string): boolean {
  return needsWebhook(type) && !isSendFiles(type);
}

const LABEL_KEYS: Record<ActionType, string> = {
  CREATE_ORDER: 'settings-actions-type-create-order',
  CREATE_RETURN: 'settings-actions-type-create-return',
  SEND_FILES: 'settings-actions-type-send-files',
  AI_REPLY: 'settings-actions-type-ai-reply',
  CONVERSATION_INFO: 'settings-actions-type-conversation-info',
};
const DESCRIPTION_KEYS: Record<ActionType, string> = {
  CREATE_ORDER: 'settings-actions-type-desc-create-order',
  CREATE_RETURN: 'settings-actions-type-desc-create-return',
  SEND_FILES: 'settings-actions-type-desc-send-files',
  AI_REPLY: 'settings-actions-type-desc-ai-reply',
  CONVERSATION_INFO: 'settings-actions-type-desc-conversation-info',
};

/** Display name; unknown (future) types fall back to the raw value. */
export function actionTypeLabel(type: string): string {
  return isActionType(type) ? t(LABEL_KEYS[type]) : type;
}

export function actionTypeDescription(type: string): string {
  return isActionType(type) ? t(DESCRIPTION_KEYS[type]) : '';
}

/**
 * Resolve a copy key that differs by type. Keys follow `<base>` for orders,
 * `<base>-return` for returns, `<base>-files` for send-files, `<base>-reply`
 * for AI Reply and `<base>-info` for Conversation info; unknown types read
 * the order copy.
 */
export function typedKey(base: string, type: string): string {
  if (type === 'CREATE_RETURN') return `${base}-return`;
  if (isSendFiles(type)) return `${base}-files`;
  if (isAiReply(type)) return `${base}-reply`;
  if (isConversationInfo(type)) return `${base}-info`;
  return base;
}

/** Select value that shows every configured action. Not an action type id. */
export const ALL_ACTION_TYPES_FILTER = 'all';

/**
 * Types this org may configure, for the list filter. The input is
 * GET /api/org-actions `allowedTypes` (the same list Add action offers).
 * Order is that list's. Blanks and repeats are dropped. Types the product
 * knows about but this org cannot use are not added.
 */
export function actionTypeFilterOptions(allowedTypes: readonly string[]): string[] {
  const seen = new Set<string>();
  const options: string[] = [];
  for (const type of allowedTypes) {
    if (!type || seen.has(type)) continue;
    seen.add(type);
    options.push(type);
  }
  return options;
}

/** The filter is offered only when the org may use two or more types. */
export function offersActionTypeFilter(allowedTypes: readonly string[]): boolean {
  return actionTypeFilterOptions(allowedTypes).length >= 2;
}

/**
 * Type to apply to the list. One available type, or a selection the org
 * cannot use, leaves the list unfiltered.
 */
export function appliedActionTypeFilter(allowedTypes: readonly string[], selected: string): string {
  if (!offersActionTypeFilter(allowedTypes) || selected === ALL_ACTION_TYPES_FILTER) return ALL_ACTION_TYPES_FILTER;
  return actionTypeFilterOptions(allowedTypes).includes(selected) ? selected : ALL_ACTION_TYPES_FILTER;
}
